import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Menu, X } from "lucide-react";

const NAV = [
  { id: "hero", label: "Home" },
  { id: "about", label: "About Saloni" },
  { id: "video-wish", label: "From Via" },
  { id: "memories", label: "Memory Lane" },
];

export default function Nav({ active, onNavigate }) {
  const [menuOpen, setMenuOpen] = useState(false);

  // Lock scroll while menu open
  useEffect(() => {
    if (menuOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  const jump = (id) => { setMenuOpen(false); setTimeout(() => onNavigate(id), 250); };

  return (
    <>
      <header
        data-testid="site-nav"
        className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#F7F9FC]/75 border-b border-[#EAF0F6]"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <button
            data-testid="brand-link"
            onClick={() => onNavigate("hero")}
            className="flex items-center gap-2 group"
          >
            <span className="w-9 h-9 rounded-full bg-[#0B192C] text-[#F7F9FC] flex items-center justify-center transition-transform duration-300 group-hover:rotate-12">
              <Sparkles className="w-4 h-4" />
            </span>
            <span className="font-display text-[#0B192C] text-base sm:text-lg tracking-tight">
              For Saloni <span className="font-hand text-[#7B9ACC]">— with love</span>
            </span>
          </button>

          <nav className="hidden md:flex items-center gap-1">
            {NAV.map((n) => (
              <button
                key={n.id}
                data-testid={`nav-${n.id}`}
                onClick={() => onNavigate(n.id)}
                className={`px-4 py-2 rounded-full text-sm font-body transition-colors duration-200 ${
                  active === n.id
                    ? "bg-[#0B192C] text-[#F7F9FC]"
                    : "text-[#334E68] hover:bg-[#EAF0F6]"
                }`}
              >
                {n.label}
              </button>
            ))}
          </nav>

          <button
            data-testid="mobile-menu-btn"
            onClick={() => setMenuOpen((v) => !v)}
            className="md:hidden w-10 h-10 rounded-full bg-[#0B192C] text-[#F7F9FC] flex items-center justify-center"
            aria-label="Menu"
            aria-expanded={menuOpen}
          >
            {menuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
        </div>
      </header>

      {/* Mobile bottom sheet */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="md:hidden fixed inset-0 z-40 bg-[#0B192C]/40 backdrop-blur-sm"
            onClick={() => setMenuOpen(false)}
            data-testid="mobile-menu-scrim"
          >
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", stiffness: 260, damping: 28 }}
              onClick={(e) => e.stopPropagation()}
              className="absolute bottom-0 inset-x-0 rounded-t-3xl bg-[#F7F9FC] pt-3 pb-8 shadow-[0_-30px_60px_-20px_rgba(11,25,44,0.35)]"
              style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 2rem)" }}
              data-testid="mobile-menu-sheet"
            >
              <div className="w-10 h-1 bg-[#DDE4EE] rounded-full mx-auto mb-3" />
              <div className="px-5">
                <p className="font-body text-[10px] uppercase tracking-[0.35em] text-[#7B9ACC]">Navigate</p>
                <h3 className="mt-1 font-display font-black text-[#0B192C] text-3xl leading-tight">Where to, Bob?</h3>
              </div>
              <div className="mt-4 px-3 flex flex-col">
                {NAV.map((n, i) => (
                  <motion.button
                    key={n.id}
                    data-testid={`mobile-nav-${n.id}`}
                    onClick={() => jump(n.id)}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.05 + i * 0.05 }}
                    className={`text-left px-4 py-4 rounded-2xl flex items-center justify-between font-display text-2xl transition-colors ${
                      active === n.id
                        ? "bg-[#0B192C] text-[#F7F9FC]"
                        : "text-[#0B192C] hover:bg-[#EAF0F6]"
                    }`}
                  >
                    <span>{n.label}</span>
                    <span className="text-lg opacity-60">→</span>
                  </motion.button>
                ))}
              </div>
              <p className="mt-4 px-5 font-hand text-[#7B9ACC] text-lg">
                psst: tap the sparkle button below-left for a lockscreen wallpaper 💙
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
